import React, {useState} from 'react';
import {Button, Card, Modal, Form, Alert} from "react-bootstrap";
import {Link, useNavigate} from 'react-router-dom';
import {deleteAccount, disableAccount, sendOTPVerification} from "./../../context/User"
import './style.css'
import logo from '../../pictures/home/logo-m.png'
import {useAuth} from "../../context/AuthContext";
import {useSiteTranslation} from "../home/i18n";
import {useLanguageSwitch} from "../home/hooks/useLanguageSwitch";

export default function DisableAccountComponent() {
    const navigate = useNavigate();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpArray, setOtpArray] = useState(Array(6).fill(''));
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [actionType, setActionType] = useState(''); // 'temporary' or 'permanent'
    const {logout} = useAuth()
    const {t, language, dir} = useSiteTranslation()
    const {setLanguage} = useLanguageSwitch()

    const handleLogout = () => {
        logout().finally(() => navigate('/login'))
    }

    const sendOtpToEmail = () => {
        setLoading(true);
        setError('');
        const requestType = actionType === 'temporary'?"disable":"delete"
        sendOTPVerification(requestType).then(() => {
            setOtpSent(true);
        }).catch(err => {
            setError(t('account.sendFailed'));
        }).finally(() => {
            setLoading(false);
        })
    };

    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return; // Only allow numbers
        const newOtpArray = [...otpArray];
        newOtpArray[index] = value;
        setOtpArray(newOtpArray);

        // Focus next input automatically
        if (value !== '' && index < otpArray.length - 1) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && otpArray[index] === '' && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    const verifyOtp = () => {
        const otp = otpArray.join('');
        if (otp.length < otpArray.length) {
            setError(t('account.incomplete'));
            return;
        }
        setError('');
        setLoading(true);
        const successMessageText =
            actionType === 'temporary'
                ? t('account.disabled')
                : t('account.deleted');

        if (actionType === 'temporary') {
            disableAccount(otp).then(res => {
                setSuccessMessage(successMessageText);
                setShowConfirmModal(false);
                setOtpSent(false);
                setOtpArray(Array(6).fill(''));
            }).catch(err => {
                setError(t('account.invalidCode'));
            }).finally(() => {
                setLoading(false);
            })
        } else {
            deleteAccount(otp).then(res => {
                setSuccessMessage(successMessageText);
                setShowConfirmModal(false);
                setOtpSent(false);
                setOtpArray(Array(6).fill(''));
            }).catch(err => {
                setError(t('account.invalidCode'));
            }).finally(() => {
                setLoading(false);
            })
        }
    };

    const handleCancel = () => {
        setShowConfirmModal(false);
        setOtpSent(false);
        setOtpArray(Array(6).fill(''));
        setError('');
        setActionType('');
    };

    return (
        <div className="account-page" dir={dir} lang={language}>
            <header className="account-header">
                <Link to="/" aria-label={t('common.brandHome')}>
                    <img src={logo} alt="MJMarry" />
                </Link>
                <div className="account-header-actions">
                    <button
                        type="button"
                        className="account-logout"
                        lang={language === 'ar' ? 'en' : 'ar'}
                        onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                    >
                        {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                    <button type="button" className="account-logout" onClick={handleLogout}>
                        {t('account.logout')}
                    </button>
                </div>
            </header>
            <div className="account-body">
                <Card className="account-card">
                    <Card.Body>
                        {!successMessage ? (
                            <>
                                <p className="account-eyebrow">{t('account.eyebrow')}</p>
                                <Card.Title as="h1" className="account-title">
                                    {t('account.title')}
                                </Card.Title>
                                <Card.Text className="account-text">
                                    {t('account.text')}
                                </Card.Text>
                                <div className="account-actions">
                                    <button
                                        type="button"
                                        className="account-action"
                                        onClick={() => {
                                            setActionType('temporary');
                                            setShowConfirmModal(true);
                                        }}
                                    >
                                        <strong>{t('account.disableTitle')}</strong>
                                        <span>{t('account.disableText')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="account-action danger"
                                        onClick={() => {
                                            setActionType('permanent');
                                            setShowConfirmModal(true);
                                        }}
                                    >
                                        <strong>{t('account.deleteTitle')}</strong>
                                        <span>{t('account.deleteText')}</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <Alert variant={actionType === 'temporary' ? 'warning' : 'success'}>
                                {successMessage}
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={handleCancel} centered className={`account-modal${dir === 'rtl' ? ' account-modal--rtl' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {otpSent ? t('account.enterCode') : actionType === 'temporary' ? t('account.confirmDisable') : t('account.confirmDelete')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!otpSent ? (
                        <div className="modal-text">
                            {actionType === 'temporary'
                                ? t('account.disableQuestion')
                                : t('account.deleteQuestion')}
                        </div>
                    ) : (
                        <>
                            <Form.Group>
                                <Form.Label>{t('account.codeLabel')}</Form.Label>
                                {/* Codes read left to right in every language. */}
                                <div className="d-flex justify-content-center" dir="ltr">
                                    {otpArray.map((digit, index) => (
                                        <Form.Control
                                            key={index}
                                            id={`otp-input-${index}`}
                                            type="text"
                                            value={digit}
                                            maxLength="1"
                                            inputMode="numeric"
                                            aria-label={t('account.digitLabel', {n: index + 1})}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            className="otp-input"
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}
                    {error && <p className="account-error" role="alert">{error}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel} disabled={loading} className="custom-button">
                        {t('account.cancel')}
                    </Button>
                    {!otpSent ? (
                        <Button
                            variant={actionType === 'temporary' ? 'warning' : 'danger'}
                            onClick={sendOtpToEmail}
                            disabled={loading}
                            className="custom-button"
                        >
                            {loading ? t('account.sending') : t('account.proceed')}
                        </Button>
                    ) : (
                        <Button
                            variant={actionType === 'temporary' ? 'warning' : 'danger'}
                            onClick={verifyOtp}
                            disabled={loading}
                            className="custom-button"
                        >
                            {loading ? t('account.verifying') : t('account.submit')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
}
